import { inject } from "fw";
import { handle } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { AtsFeatureFlagsRepository } from "network/ats-feature-flags-repository";

import { FeatureFlagOrgInfo, FeatureFlagsDetailsFilter } from "models/feature-flags";
import { GridStore } from "./components/grid";

const CACHE_KEY = "ff-details-store";
const FILTER_FIELDS_MAPPING: Map<string, string> = new Map().set("Organization", "metaData.organizationId");

export class FFDetailsNextPageAction {}
export class FFDetailsPreviousPageAction {}

export class FFDetailsUpdateRefreshDateAction {
    constructor(public date: Date = new Date()) {}
}

export class UpdateOrgFFAction {
    constructor(
        public id: string,
        public value: boolean
    ) {}
}

export class FFDetailsFetchListAction<Filter> {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public sort: string = null
    ) {}
}

@inject
export class FeatureFlagsDetailsStore extends GridStore<FeatureFlagOrgInfo, FeatureFlagsDetailsFilter> {
    constructor(private network: AtsFeatureFlagsRepository) {
        super(CACHE_KEY);
    }

    @handle(StartAction)
    private startAction(action: StartAction) {
        this.handleStartAction(action);
    }

    @handle(LogoutAction)
    private logoutAction() {
        this.handleLogoutAction();
    }

    @handle(UpdateOrgFFAction)
    private handleUpdateOrgFFAction(action: UpdateOrgFFAction) {
        return this.setState((state) => ({
            ...state,
            list: [
                ...state.list.filter((org) => org.OrganizationId !== action.id),
                { ...state.list.filter((org) => org.OrganizationId === action.id)[0], Value: !action.value }
            ]
        }));
    }

    @handle(FFDetailsUpdateRefreshDateAction)
    private updateRefreshedDate(action: FFDetailsUpdateRefreshDateAction) {
        this.setState((state) => ({
            ...state,
            updatedDate: action.date
        }));
    }

    @handle(FFDetailsFetchListAction)
    private fetchListAction(action: FFDetailsFetchListAction<FeatureFlagsDetailsFilter>) {
        this.handleFetchList(action, () => {
            this.loadFFDetailsList();
        });
    }

    @handle(FFDetailsNextPageAction)
    private nextPageAction(action: FFDetailsNextPageAction) {
        this.handleNextPageAction(action, async (nextPageToken) => {
            await this.loadFFDetailsPageData(null, nextPageToken);
        });
    }

    @handle(FFDetailsPreviousPageAction)
    private previousPageAction(action: FFDetailsPreviousPageAction) {
        this.handlePreviousPage(action, async (previousPageToken) => {
            await this.loadFFDetailsPageData(previousPageToken, null);
        });
    }

    private async loadFFDetailsList() {
        const { currentPreviousPageToken, currentNextPageToken } = this.state;
        this.setState((state) => ({
            ...state,
            filter: {
                ...state.filter
            }
        }));
        await this.loadFFDetailsPageData(currentPreviousPageToken, currentNextPageToken);
    }

    private async loadFFDetailsPageData(previousPageToken: string, nextPageToken: string) {
        const { pageSize, sort, filter } = this.state;

        try {
            const resp = await this.network.getFlagDetails(filter.Flag, this.getWholeFilter(), sort, previousPageToken, nextPageToken, pageSize);

            this.setState((state) => ({
                ...state,
                loaded: true,
                previousPageToken: resp.PreviousPageToken,
                nextPageToken: resp.NextPageToken,
                currentPreviousPageToken: previousPageToken,
                currentNextPageToken: nextPageToken,
                list: resp.Results,
                total: resp.Total
            }));
        } catch (err) {
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true }));
        }
    }

    public getWholeFilter(): string {
        const { filter } = this.state;

        if (!Object.keys(filter).length) return null;

        const filterArgs = [];
        for (const [key, value] of Object.entries(filter)) {
            if (value === undefined || value === null) continue;

            if (key === "Flag") continue;

            filterArgs.push(`${FILTER_FIELDS_MAPPING.get(key)}:${value}`);
        }

        return filterArgs.join(" AND ");
    }
}
